/**
 * Mana Pricing email templates.
 *
 *  - The borrower's best-rates email has one template per loan program / purpose (purchase,
 *    rate/term refinance, cash-out, DSCR investor, home equity): same rate grid, its own subject,
 *    headline and intro. The page defaults the template from the scenario; the MLO can pick
 *    another.
 *  - The Realtor Marketing email is its own format: five loan programs (30-yr conventional 5/6 ARM,
 *    30-yr fixed conventional, lender-paid 2/1 buydown, 30-yr fixed FHA, 30-yr FHA 5/1 ARM) × three price columns. Par = the results'
 *    "Best rate": the lowest ★ rate across the picked lenders (the rate whose lender credit meets
 *    the MLO's comp + closing criteria), falling back to the rate at the scenario's desired price
 *    (the results' "desired price" rate) only when no option has a ★ rate; 1 point / 2 points =
 *    the lowest rate whose price is within 1 / 2 points below that par rate's price — so every
 *    cell carries a rate whenever the program priced at all — or the rate the MLO pinned from
 *    the results screen.
 */
import type { ManaPricingSource } from './schemas.js';
import type { ManaRateCandidate } from './schemas.js';

// ---------------------------------------------------------------------------
// Borrower templates
// ---------------------------------------------------------------------------

export type ManaEmailTemplate = 'purchase' | 'refinance' | 'cash_out' | 'dscr' | 'home_equity';
export const MANA_EMAIL_TEMPLATES: readonly ManaEmailTemplate[] = ['purchase', 'refinance', 'cash_out', 'dscr', 'home_equity'];
export const MANA_EMAIL_TEMPLATE_LABELS: Record<ManaEmailTemplate, string> = {
  purchase: 'Home purchase',
  refinance: 'Rate/term refinance',
  cash_out: 'Cash-out refinance',
  dscr: 'DSCR investor',
  home_equity: 'Home equity',
};

/** The template a scenario calls for: its product type, loan type and purpose. */
export function defaultEmailTemplate(inputs: Record<string, unknown> | null | undefined, productType: string | null | undefined): ManaEmailTemplate {
  const s = (v: unknown) => (Array.isArray(v) ? v.map((x) => String(x)).join(' ') : String(v ?? ''));
  if (productType === 'heloc' || /heloc/i.test(s(inputs?.loanType)) || /second/i.test(s(inputs?.lienPosition))) return 'home_equity';
  if (/^\s*dscr\s*$/i.test(s(inputs?.loanType)) || (/dscr/i.test(s(inputs?.loanType)) && !/conventional|fha|va|usda|jumbo/i.test(s(inputs?.loanType)))) return 'dscr';
  const purpose = s(inputs?.loanPurpose);
  if (/cash/i.test(purpose)) return 'cash_out';
  if (/refi|rate/i.test(purpose)) return 'refinance';
  return 'purchase';
}

export interface ManaEmailTemplateCopy {
  /** "Your … Loan Options today" — `purpose` is the LOAN PARAMETERS purpose label ("Cash-Out"). */
  subject: (purpose: string) => string;
  pill: string;
  /** May carry a <br>. */
  heading: (purpose: string) => string;
  intro: string;
  /** Plain-text heading before the rate list. */
  textHeading: (purpose: string) => string;
}
export const MANA_EMAIL_TEMPLATE_COPY: Record<ManaEmailTemplate, ManaEmailTemplateCopy> = {
  purchase: {
    subject: () => 'Your Home Purchase Loan Options today',
    pill: 'HOME PURCHASE OPTIONS',
    heading: () => 'Home Purchase Loan Options',
    intro: "Fresh rates, real savings. Let's find the right loan for your new home.",
    textHeading: () => 'Home Purchase Mortgage Loan Options:',
  },
  refinance: {
    subject: () => 'Your Refinance Loan Options today',
    pill: 'REFINANCE OPTIONS',
    heading: () => '$0 Front-End Cost<br>Refinance Mortgage Loan Options',
    intro: 'Lower your rate or your payment — here are today’s refinance options.',
    textHeading: () => '$0 Front-End Cost Refinance Mortgage Loan Options:',
  },
  cash_out: {
    subject: () => 'Your Cash-Out Refinance Options today',
    pill: 'CASH-OUT REFINANCE OPTIONS',
    heading: () => 'Cash-Out Refinance Loan Options',
    intro: 'Put your equity to work — today’s cash-out refinance options.',
    textHeading: () => 'Cash-Out Refinance Mortgage Loan Options:',
  },
  dscr: {
    subject: (purpose) => `Your DSCR ${purpose || 'Investor'} Loan Options today`,
    pill: 'DSCR INVESTOR OPTIONS',
    heading: (purpose) => `DSCR ${purpose || 'Investor'} Loan Options`,
    intro: 'Investor cash-flow pricing — qualified on the property’s rent, no tax returns.',
    textHeading: (purpose) => `DSCR ${purpose || 'Investor'} Mortgage Loan Options:`,
  },
  home_equity: {
    subject: () => 'Your Home Equity Options today',
    pill: 'HOME EQUITY OPTIONS',
    heading: () => 'Home Equity Loan Options',
    intro: 'Keep your first mortgage and borrow against your equity.',
    textHeading: () => 'Home Equity Loan Options:',
  },
};

// ---------------------------------------------------------------------------
// Realtor Marketing
// ---------------------------------------------------------------------------

export type RealtorRowKey = 'arm_5_6_30' | 'fixed_30' | 'lp_2_1_30' | 'fha_fixed_30' | 'fha_arm_5_1_30';
export const REALTOR_ROWS: ReadonlyArray<{ key: RealtorRowKey; label: string; /** What the scenario must price for this row. */ needs: string }> = [
  { key: 'arm_5_6_30', label: '30-yr conventional 5/6 ARM', needs: 'Amortization ARM (5/6), 30-year term' },
  { key: 'fixed_30', label: '30-yr fixed conventional', needs: 'Amortization Fixed, 30-year term, buydown None' },
  { key: 'lp_2_1_30', label: 'Lender-paid 2/1 buydown', needs: 'Temporary Buydown "2/1 Lender-paid", 30-year fixed' },
  { key: 'fha_fixed_30', label: '30-yr fixed FHA', needs: 'Loan Type FHA, Amortization Fixed, 30-year term, buydown None' },
  // FHA ARMs adjust yearly (a "5/1"); the ARM Type field names the five-year period "5/6" and the lenders' FHA engines answer with their 5/1.
  { key: 'fha_arm_5_1_30', label: '30-yr FHA 5/1 ARM', needs: 'Loan Type FHA, Amortization ARM (5/6), 30-year term, buydown None' },
];
export type RealtorColumnKey = 'par' | 'onePoint' | 'twoPoints';
export const REALTOR_COLUMNS: ReadonlyArray<{ key: RealtorColumnKey; label: string; short: string; maxPoints: number }> = [
  { key: 'par', label: 'Par rate', short: 'Par', maxPoints: 0 },
  { key: 'onePoint', label: '1 point charge', short: '1 pt', maxPoints: 1 },
  { key: 'twoPoints', label: '2 points charge', short: '2 pts', maxPoints: 2 },
];

/** A rate the MLO pinned for a cell from the results screen (the option and its rate). */
export interface RealtorPick {
  optionId: string;
  rate: number;
}
export type RealtorPicks = Partial<Record<RealtorRowKey, Partial<Record<RealtorColumnKey, RealtorPick>>>>;

export interface RealtorLadderPoint {
  rate: number;
  /** 100-based price as the results show it (net of comp when lender-paid). */
  price: number;
  apr: number;
  pi: number;
  /** Lender credit in dollars (+) or cost (−). */
  credit: number;
  /** ★ — the credit at this rate meets the MLO's comp + closing criteria (the results' "Best rate" candidates). */
  star?: boolean;
}
/** One priced option (UWM product or lender row) as the realtor table sees it. */
export interface RealtorCandidateRow {
  optionId: string;
  source: ManaPricingSource;
  lender: string;
  product: string;
  termYears: number;
  amortization: 'fixed' | 'arm';
  armType: string | null;
  buydown: { base: string; paidBy: 'lender' | 'borrower' } | null;
  loanType: string | null;
  asOf: string | null;
  ladder: RealtorLadderPoint[];
}

/** ARM fixed periods the lenders offer (years) — a "30 Yr ARM" is the term, not the period. */
const ARM_PERIODS = new Set([3, 5, 7, 10]);
/** The ARM type a product name carries ("7/6 SOFR ARM", "30 Yr ARM 5yr / 6mo", "5 Year ARM", "CA5/6"); null when it doesn't say. */
export function armOfName(name: string): string | null {
  const s = name.toUpperCase();
  const patterns = [
    /(\d{1,2})\s*\/\s*(?:6|1)(?!\d)/g, // 5/6, 7/1
    /(\d{1,2})\s*(?:YR|YEAR)S?\s*\/\s*(?:6|1)\s*(?:MO|MONTH)/g, // 5yr / 6mo
    /(\d{1,2})\s*(?:YR|YEAR)S?\s*(?:FIXED\s*)?ARM/g, // 5 Year ARM
    /ARM\s*(\d{1,2})\s*(?:YR|YEAR)/g, // ARM 5 Yr
  ];
  for (const re of patterns) {
    for (const m of s.matchAll(re)) {
      const n = Number(m[1]);
      if (ARM_PERIODS.has(n)) return `${n}/6`;
    }
  }
  return null;
}

/** The scenario's ARM Type pick when it names one ("5/6"), null for "All" / blank. */
export function specificArmType(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return /^\d{1,2}\/\d$/.test(s) ? s : null;
}

/** Which realtor row an option belongs to (null = none: another term, loan type, ARM type or buydown). */
export function realtorRowOf(r: Pick<RealtorCandidateRow, 'termYears' | 'amortization' | 'armType' | 'buydown' | 'loanType' | 'product'>): RealtorRowKey | null {
  if (r.termYears !== 30) return null;
  const type = (r.loanType ?? '').trim();
  // FHA: the 30-year fixed and the five-year ARM, no buydown (a UWM row without a family tag says FHA in its product name).
  const isFha = /fha/i.test(type) || (!type && /\bfha\b/i.test(r.product));
  if (isFha) {
    if (r.buydown) return null;
    if (r.amortization !== 'arm' && !/\barm\b/i.test(r.product)) return 'fha_fixed_30';
    // The lenders name their FHA ARM "5/1" (yearly adjustments) and the scenario's pick says "5/6": both are the five-year ARM.
    const arm = armOfName(r.product) ?? r.armType;
    return arm && /^5\//.test(arm) ? 'fha_arm_5_1_30' : null;
  }
  if (type && !/^conv/i.test(type) && !/conventional/i.test(type)) return null;
  if (!type && /\b(fha|va|usda|jumbo|dscr)\b/i.test(r.product)) return null;
  if (r.amortization === 'arm') {
    if (r.buydown) return null;
    // The product name says which ARM it is when the lender offers several; otherwise the ARM
    // type the scenario asked for (a specific one, not "All").
    const arm = armOfName(r.product) ?? r.armType;
    return arm === '5/6' ? 'arm_5_6_30' : null;
  }
  if (/\barm\b/i.test(r.product)) return null;
  if (!r.buydown) return 'fixed_30';
  return r.buydown.base === '2-1' && r.buydown.paidBy === 'lender' ? 'lp_2_1_30' : null;
}

/** The ladder point at the scenario's desired price (closest price, tie → lower rate) — the rate the results mark "desired price". */
export function realtorParPoint(ladder: RealtorLadderPoint[], desiredPrice: number): RealtorLadderPoint | null {
  let best: RealtorLadderPoint | null = null;
  for (const p of ladder) {
    if (!best) {
      best = p;
      continue;
    }
    const d = Math.abs(p.price - desiredPrice);
    const bd = Math.abs(best.price - desiredPrice);
    if (d < bd - 1e-9 || (Math.abs(d - bd) < 1e-9 && p.rate < best.rate)) best = p;
  }
  return best;
}

/** An option's ★ rate — the lowest rate that meets the MLO's criteria (the results' "Best rate") — or null. */
export function realtorStarPoint(ladder: RealtorLadderPoint[]): RealtorLadderPoint | null {
  return [...ladder].sort((a, b) => a.rate - b.rate).find((p) => p.star) ?? null;
}

/** An option's headline as the results show it: its ★ rate, else the rate at the desired price. */
export function realtorHeadlinePoint(ladder: RealtorLadderPoint[], desiredPrice: number): { point: RealtorLadderPoint; star: boolean } | null {
  const star = realtorStarPoint(ladder);
  if (star) return { point: star, star: true };
  const p = realtorParPoint(ladder, desiredPrice);
  return p ? { point: p, star: false } : null;
}

/**
 * The lowest price a point column accepts: a TOTAL charge of at most 1 / 2 discount points
 * (price 99.000 / 98.000), whatever the row's par rate costs; null for the par column. (Until
 * 2026-10-11 the columns were measured from the par price — "1 point over par" — which, with
 * par carrying a lender credit, charged the buyer well under a point; the realtors want the rate
 * a buyer gets by spending a full point or two.)
 */
export function realtorPriceFloor(column: RealtorColumnKey): number | null {
  const max = REALTOR_COLUMNS.find((c) => c.key === column)!.maxPoints;
  return column === 'par' ? null : 100 - max - 0.0005;
}

/**
 * The ladder point a column shows for one option. Par: the pinned rate, else the option's ★
 * rate, else the rate at the desired price. 1 / 2 points: the pinned rate while its total
 * discount points are at most 1 / 2, else the lowest rate costing at most 1 / 2 points in total —
 * which sits ABOVE the par rate on a day par itself costs more than that (an ARM priced far below
 * 100), and is null when no rate of the option fits the budget.
 */
export function realtorPointFor(ladder: RealtorLadderPoint[], column: RealtorColumnKey, desiredPrice: number, pinnedRate?: number | null): { point: RealtorLadderPoint; pinned: boolean; star: boolean } | null {
  const sorted = [...ladder].sort((a, b) => a.rate - b.rate);
  const pinnedPoint = pinnedRate != null ? (sorted.find((x) => Math.abs(x.rate - pinnedRate) < 1e-6) ?? null) : null;
  if (column === 'par') {
    if (pinnedPoint) return { point: pinnedPoint, pinned: true, star: !!pinnedPoint.star };
    const h = realtorHeadlinePoint(ladder, desiredPrice);
    return h ? { point: h.point, pinned: false, star: h.star } : null;
  }
  const floor = realtorPriceFloor(column)!;
  if (pinnedPoint && pinnedPoint.price >= floor) return { point: pinnedPoint, pinned: true, star: !!pinnedPoint.star };
  const p = sorted.find((x) => x.price >= floor);
  return p ? { point: p, pinned: false, star: !!p.star } : null;
}

export interface RealtorWinner extends ManaRateCandidate {
  optionId: string;
  apr: number;
  pi: number;
  /** Chosen by the MLO on the results screen (not the rule). */
  pinned: boolean;
  /** ★ — this rate meets the MLO's comp + closing criteria (the results' "Best rate"). */
  star: boolean;
  /** The row's par price this cell is measured from (the par cell's own price for the par column). */
  parPrice: number;
}
export interface RealtorCell {
  row: RealtorRowKey;
  column: RealtorColumnKey;
  winner: RealtorWinner | null;
  /** Every option's rate for this cell (lowest first). */
  candidates: ManaRateCandidate[];
}

const SOURCE_ORDER: ManaPricingSource[] = ['uwm', 'fcm', 'pennymac', 'sunwest', 'amwest', 'tls', 'newrez', 'cardinal', 'springeq'];

/**
 * The 5 × 3 realtor table from the priced options. Per row the par cell is the pinned rate, else
 * the results' Best rate: the lowest ★ rate across the options (a tie goes to the better price,
 * then to the source order); when no option has a ★ rate, the desired-price rate closest to the
 * desired price across the options (then the lowest rate), so a day no program prices near par
 * shows the rate that costs the fewest points. The 1 / 2 point columns are the lowest rates across
 * the options whose total discount points are at most 1 / 2 (a pinned rate wins while it fits) —
 * independent of the par price, and empty when no rate fits the budget. `notes` says when a
 * pinned rate could not be used or a point column has nothing to show.
 */
export function buildRealtorTable(rows: RealtorCandidateRow[], picks: RealtorPicks | null | undefined, desiredPrice = 100): { cells: RealtorCell[]; notes: string[] } {
  const cells: RealtorCell[] = [];
  const notes: string[] = [];
  for (const row of REALTOR_ROWS) {
    const options = rows.filter((r) => realtorRowOf(r) === row.key);
    let parPrice: number | null = null;
    for (const col of REALTOR_COLUMNS) {
      const pick = picks?.[row.key]?.[col.key] ?? null;
      const found: RealtorWinner[] = [];
      for (const opt of options) {
        const hit = realtorPointFor(opt.ladder, col.key, desiredPrice, pick && pick.optionId === opt.optionId ? pick.rate : null);
        if (!hit) continue;
        found.push({ source: opt.source, lender: opt.lender, product: opt.product, rate: hit.point.rate, price: hit.point.price, credit: hit.point.credit, asOf: opt.asOf, optionId: opt.optionId, apr: hit.point.apr, pi: hit.point.pi, pinned: hit.pinned, star: hit.star, parPrice: parPrice ?? hit.point.price });
      }
      // Pinned first. Par: the ★ rates before the desired-price fallbacks, and among fallbacks the
      // price closest to the desired price. Then the lowest rate, the better price, the source order.
      const closeness = (w: RealtorWinner) => Math.abs(w.price - desiredPrice);
      found.sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) ||
          (col.key === 'par' ? Number(b.star) - Number(a.star) || (!a.star && !b.star && Math.abs(closeness(a) - closeness(b)) > 1e-9 ? closeness(a) - closeness(b) : 0) : 0) ||
          a.rate - b.rate ||
          b.price - a.price ||
          SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source),
      );
      if (pick && !found.some((f) => f.pinned)) {
        notes.push(
          col.key === 'par'
            ? `${row.label} · ${col.label}: the pinned ${pick.rate.toFixed(3)}% is no longer offered — the best rate was used instead.`
            : `${row.label} · ${col.label}: the pinned ${pick.rate.toFixed(3)}% now costs more than ${col.maxPoints} point${col.maxPoints === 1 ? '' : 's'} in total — the lowest qualifying rate was used instead.`,
        );
      }
      const winner = found[0] ?? null;
      if (col.key === 'par') parPrice = winner?.price ?? null;
      else if (!winner && options.length) notes.push(`${row.label} · ${col.label}: no rate costs as little as ${col.maxPoints} point${col.maxPoints === 1 ? '' : 's'} today (the cell says so).`);
      cells.push({ row: row.key, column: col.key, winner, candidates: found.map(({ source, lender, product, rate, price, credit, asOf }) => ({ source, lender, product, rate, price, credit, asOf })) });
    }
  }
  return { cells, notes };
}

/** The realtor rows the priced options cannot fill (what the scenario still needs to price). */
export function realtorGaps(rows: RealtorCandidateRow[]): Array<(typeof REALTOR_ROWS)[number]> {
  return REALTOR_ROWS.filter((row) => !rows.some((r) => realtorRowOf(r) === row.key));
}

/** "no points" / "0.63 pt" / "0.25 credit" for a cell's price. */
export function realtorPointsLabel(price: number): string {
  const pts = Math.round((100 - price) * 1000) / 1000;
  if (Math.abs(pts) < 0.0005) return 'no points';
  if (pts < 0) return `${(-pts).toFixed(3)} credit`;
  return `${pts.toFixed(3)} pt${pts === 1 ? '' : 's'}`;
}

/** Points a 1 / 2 point cell costs over the row's par rate ("1.058 pts over par") — informational only since the columns became true point charges (2026-10-11); null for the par column or when it is at or above the par price. */
export function realtorOverParLabel(winner: Pick<RealtorWinner, 'price' | 'parPrice'>, column: RealtorColumnKey): string | null {
  if (column === 'par') return null;
  const over = Math.round((winner.parPrice - winner.price) * 1000) / 1000;
  if (over < 0.0005) return null;
  return `${over.toFixed(3)} pt${over === 1 ? '' : 's'} over par`;
}
